import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { cn } from '@/lib/cn';
import { findRouteByPath } from '@/app/router/routes';
import { useUiPreference } from '@/lib/preferences/use-ui-preference';
import { TooltipProvider } from '@/components/primitives/tooltip';
import { SidebarNavigation } from './sidebar-navigation';
import { TopToolbar } from './top-toolbar';
import { AssistantPanel } from './assistant-panel';
import { useOptionalWorkspace } from '@/app/providers/workspace-provider';
import { useOptionalDetailDrawer } from '@/app/providers/detail-drawer-provider';
import { QuickCaptureDialog } from '@/features/quick-capture/quick-capture-dialog';
import { NotificationScanner } from '@/features/reminders/notification-scanner';
import { SearchDialog } from '@/features/search/search-dialog';
import { UpdateNotice } from '@/platform/update-notice';

/** 最低正式驗收寬度；更窄時優先自動收合助理區（spec 7.2）。 */
const NARROW_QUERY = '(max-width: 1279px)';

export function AppShell() {
  const location = useLocation();
  const route = findRouteByPath(location.pathname);

  const [sidebarCollapsed, setSidebarCollapsed] = useUiPreference('ui.sidebar.collapsed', false);
  const [assistantCollapsed, setAssistantCollapsed] = useUiPreference(
    'ui.assistant.collapsed',
    false,
  );

  const workspace = useOptionalWorkspace();
  const drawer = useOptionalDetailDrawer();
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // 視窗變窄時自動收合助理區；使用者仍可手動再展開。
  useEffect(() => {
    let media: MediaQueryList;
    try {
      media = window.matchMedia(NARROW_QUERY);
    } catch {
      return;
    }
    const apply = (matches: boolean) => {
      if (matches) setAssistantCollapsed(true);
    };
    apply(media.matches);
    const onChange = (event: MediaQueryListEvent) => apply(event.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, [setAssistantCollapsed]);

  return (
    <TooltipProvider delayDuration={280} skipDelayDuration={200}>
      {workspace && <NotificationScanner onOpenTask={(taskId) => drawer?.openTask(taskId)} />}
      <div className="flex h-[100dvh] w-full overflow-hidden bg-bg text-text">
        <SidebarNavigation
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((current) => !current)}
        />

        <div className="flex min-w-0 flex-1 flex-col py-4">
          {/* 主要工作區同樣採 double bezel，讓中央像一塊放在桌面上的板子。 */}
          <div className="bezel-shell flex min-h-0 flex-1 flex-col rounded-[var(--radius-shell)] p-1.5">
            <div className="bezel-core flex min-h-0 flex-1 flex-col overflow-hidden rounded-[calc(var(--radius-shell)-0.375rem)]">
              <TopToolbar
                title={route?.title ?? '找不到頁面'}
                purpose={route?.purpose ?? '這個網址沒有對應的頁面'}
                saveState={workspace?.saveState ?? 'idle'}
                lastSavedAt={workspace?.lastSavedAt ?? null}
                onRetrySave={() => void workspace?.save.retry()}
                {...(workspace ? { mirror: workspace.mirrorHealth } : {})}
                onRetryMirror={() => void workspace?.mirror.flush()}
                onQuickCapture={() => setQuickCaptureOpen(true)}
                quickCaptureDisabled={!(workspace?.isSeeded ?? false)}
                onSearch={() => setSearchOpen(true)}
              />
              <div className="h-px shrink-0 bg-border" />
              <main
                className={cn(
                  'min-h-0 flex-1',
                  route?.fullBleed ? 'overflow-hidden' : 'overflow-y-auto px-6 py-6',
                )}
              >
                {route?.fullBleed ? (
                  <Outlet />
                ) : (
                  <div className="mx-auto w-full max-w-[68rem]">
                    <Outlet />
                  </div>
                )}
              </main>
            </div>
          </div>
        </div>

        <AssistantPanel
          collapsed={assistantCollapsed}
          onToggle={() => setAssistantCollapsed((current) => !current)}
        />

        {searchOpen && workspace && <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />}

        {/* 桌面版才會真的渲染出東西；網頁版重新整理本來就是最新的。 */}
        <UpdateNotice />

        {quickCaptureOpen && (
          <QuickCaptureDialog
            open={quickCaptureOpen}
            onOpenChange={setQuickCaptureOpen}
            onCreated={(taskId) => drawer?.openTask(taskId)}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
